import React, { useEffect, useRef, useState } from "react";
import { scoreColor, strokeBadge } from "@/lib/captureTheme";

const GAP_ROOMY = 4; // gap-1 — the normal spacing
const GAP_DENSE = 1; // dense sessions: keeps 70+ bars inside a 360px rail
const DENSE_REPS = 40; // above this, 4px gaps alone would eat the whole column
const BADGE_MIN_PX = 20; // a 2-char label at 8px, plus breathing room

/**
 * Spacing between bars for a given rep count.
 *
 * With 4px gaps, 77 bars spend 304px of the width on gaps alone — more than the
 * entire 360px left rail. Tightening the gap on dense sessions is what lets the
 * bars actually fit instead of overflowing the column.
 */
export function gapFor(repCount) {
  return repCount > DENSE_REPS ? GAP_DENSE : GAP_ROOMY;
}

/**
 * True when every bar is wide enough to carry a 2-char label without colliding.
 *
 * Exported because this is the whole fix for the first reported overlap, and it
 * is worth testing directly rather than only through the DOM. Note the default:
 * an UNMEASURED container (width 0 — server render, or before the first
 * ResizeObserver callback) suppresses labels. Failing closed matters here: the
 * bug was labels crowding each other, so the safe default is to show none.
 *
 * Uses the same gap the bars are actually rendered with, so the decision cannot
 * drift from reality.
 */
export function badgesFit(repCount, containerWidth) {
  if (!repCount || !containerWidth) return false;
  const perBar = (containerWidth - (repCount - 1) * gapFor(repCount)) / repCount;
  return perBar >= BADGE_MIN_PX;
}

/**
 * Per-rep score bars, shared by the coach detail page and the read-only analysis
 * renderer (student view + public share link).
 *
 * ---- Why this exists as a component ----
 *
 * The bars were duplicated inline in CaptureAnalysis.jsx and CaptureSessions.jsx.
 * The stroke-type badges were absolutely positioned at `-top-3` on each bar,
 * which meant every label lived in the SAME horizontal band as its neighbours
 * and, on a session with many reps, overflowed its own bar.
 *
 * ---- Bug 1: badges collided and bled upward onto the video ----
 *
 *   1. Badges get their OWN flex row above the bars, so a badge can never move
 *      with its bar's height and can never overlap a neighbour. Layout, not
 *      absolute offsets.
 *   2. The row is only rendered when each bar is wide enough to hold a label.
 *      Measured with a ResizeObserver rather than guessed from a rep count,
 *      because the same component renders in a ~360px rail and in the wider
 *      analysis panel. When labels don't fit they are dropped, and the hover
 *      title still carries rep number, score, stroke type and timestamp.
 *   3. Faint gridlines give the eye a scale, since the bars have no axis.
 *
 * ---- Bug 2: the bars overflowed the left rail and covered the AI report ----
 *
 * Both callers place this component in `grid lg:grid-cols-[360px_1fr]` — a FIXED
 * 360px left rail beside the biomechanics report. Every bar carried
 * `minWidth: 4`, so a 77-rep session demanded 77x4 + 76x4 = 612px of an
 * unshrinkable flex row inside a 360px column. Nothing clipped it, so ~250px of
 * bars spilled into column two and were painted straight over the report text.
 *
 * Three independent guards now, because one was clearly not enough:
 *   a. bars are allowed to SHRINK (`minWidth: 0`, `flex-basis: 0`), so the row
 *      fits the space it was given rather than demanding its own;
 *   b. `min-w-0` on the wrapper, so a grid/flex parent can narrow it (a grid
 *      item's default `min-width: auto` refuses to shrink below content);
 *   c. `overflow-hidden` on the wrapper, so even a future regression cannot
 *      paint outside this component's box and cover a neighbouring column.
 *
 * @param {Array}   reps        rep records: { index, time_s, score, stroke_type }
 * @param {string?} clipSrc     clip URL; when absent the bars are not clickable
 * @param {function} onSeek     called with a timestamp in seconds, to jump the video
 * @param {number}  barHeight   px height of the bar area
 */
export default function RepSparkline({ reps, clipSrc, onSeek, barHeight = 64, className = "" }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const list = Array.isArray(reps) ? reps : [];

  // Track the container width so the badge decision is made against real space.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    // Measure first, then observe: the observer's first callback (async in a real
    // browser, synchronous in a jsdom mock) must not be clobbered by a stale
    // fallback read.
    setWidth(el.getBoundingClientRect().width || 0);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (list.length === 0) return null;

  const showBadges = badgesFit(list.length, width);
  const gap = gapFor(list.length);

  const badgeFor = (r) => strokeBadge(r.stroke_type);

  return (
    <div
      ref={wrapRef}
      className={`overflow-hidden min-w-0 ${className}`}
      data-testid="rep-sparkline"
    >
      {/* Badge row — its own band, so labels are independent of bar height. */}
      {showBadges && (
        <div className="flex items-end h-3 mb-1" style={{ gap }} aria-hidden="true">
          {list.map((r) => (
            <span
              key={`b-${r.index ?? r.time_s}`}
              className="flex-1 text-center text-[8px] leading-none font-bold text-white/60 truncate"
              style={{ minWidth: 0 }}
            >
              {badgeFor(r) || ""}
            </span>
          ))}
        </div>
      )}

      {/* Bar area. Gridlines sit behind the bars for scale. */}
      <div className="relative" style={{ height: barHeight }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {[25, 50, 75].map((pct) => (
            <div
              key={pct}
              className="absolute left-0 right-0 border-t border-white/5"
              style={{ bottom: `${pct}%` }}
            />
          ))}
        </div>

        <div
          className="relative flex items-end min-w-0"
          style={{ height: barHeight, gap }}
        >
          {list.map((r) => {
            const s = r.score ?? 0;
            const clickable = !!clipSrc && Number.isFinite(r.time_s);
            const label = `Rep ${r.index ?? "?"}: ${s}${r.stroke_type ? ` · ${r.stroke_type}` : ""} @ ${
              r.time_s?.toFixed?.(1) ?? "?"
            }s${clickable ? " · click to jump" : ""}`;
            return (
              <button
                key={r.index ?? r.time_s}
                type="button"
                onClick={() => clickable && onSeek?.(r.time_s)}
                disabled={!clickable}
                title={label}
                aria-label={label}
                className={`transition-opacity hover:opacity-80 ${clickable ? "cursor-pointer" : "cursor-default"}`}
                style={{
                  // flex: 1 1 0% + minWidth 0 => the row fits its container and the
                  // bars shrink to share it. A pixel min-width here is the bug.
                  flex: "1 1 0%",
                  minWidth: 0,
                  padding: 0,
                  height: `${Math.max(6, s)}%`,
                  background: scoreColor(s),
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
